import React, { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, HeartPulse, Stethoscope, UserRound, Wind } from "lucide-react";

const GURU_IMAGES = [
  "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=900&q=80",
];

const ICONS = {
  "beginner-training": GraduationCap,
  pranayam: Wind,
  "meditation-prana": HeartPulse,
  "doctor-guidance": Stethoscope,
};

const ROLES = [
  {
    icon: GraduationCap,
    title: "Beginner Training",
    detail: "Certified yoga trainer guiding first-timers through posture, alignment and safe progression.",
  },
  {
    icon: Wind,
    title: "Pranayam & Breathwork",
    detail: "Classical breathing practices taught in the traditional order, with counts adjusted for each body.",
  },
  {
    icon: HeartPulse,
    title: "Meditation & Prana Healing",
    detail: "Longer sittings, yoga nidra and prana-based recovery for stress and fatigue.",
  },
  {
    icon: Stethoscope,
    title: "Doctor-led Guidance",
    detail: "Practising doctors review conditions such as thyroid, back pain or diabetes before any practice is prescribed.",
  },
];

export const YogaGurus = () => {
  const [specialities, setSpecialities] = useState(null);

  useEffect(() => {
    api.get("/yoga/gurus").then(({ data }) => setSpecialities(data.specialities)).catch(() => setSpecialities([]));
  }, []);

  const cards = (specialities && specialities.length)
    ? specialities.map((s) => ({
        icon: ICONS[s.slot] || GraduationCap,
        title: s.title,
        detail: s.detail,
        guru: s.guru,
        slot: s.slot,
      }))
    : ROLES.map((r) => ({ ...r, guru: null, slot: r.title }));

  return (
  <section className="border-t border-border bg-accent/30" id="gurus" data-testid="yoga-gurus">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
      <div className="max-w-3xl">
        <div className="overline mb-3">Our Yoga Gurus</div>
        <h2 className="font-display text-4xl lg:text-5xl mb-5">Taught by certified trainers and doctors</h2>
        <p className="text-muted-foreground leading-relaxed">
          Every guru on our panel is either a certified yoga trainer or a practising doctor, and each one
          carries a different specialised responsibility — beginner training, pranayam, meditation, prana
          healing and clinical guidance. You are never handed a generic routine: the guru whose speciality
          matches your need takes your practice. Individual profiles appear here as each guru is assigned.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
        {cards.map(({ icon: Icon, title, detail, guru, slot }) => (
          <Card key={slot} className="border-border bg-card/80 backdrop-blur-sm" data-testid={`guru-role-${title.split(" ")[0].toLowerCase()}`}>
            <CardContent className="p-6">
              {guru ? (
                <>
                  <span className="w-16 h-16 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center mb-4">
                    {guru.photo
                      ? <img src={mediaUrl(guru.photo)} alt={guru.name} className="w-full h-full object-cover"/>
                      : <UserRound size={22} className="text-muted-foreground"/>}
                  </span>
                  <div className="overline mb-1">{title}</div>
                  <div className="font-display text-xl leading-tight" data-testid={`guru-public-name-${slot}`}>{guru.name}</div>
                  <div className="text-xs text-secondary mt-1">
                    {guru.qualification}
                    {guru.years_of_practice ? ` · ${guru.years_of_practice} yrs` : ""}
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{guru.note || detail}</p>
                </>
              ) : (
                <>
                  <span className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon size={19} />
                  </span>
                  <div className="font-display text-xl leading-tight">{title}</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{detail}</p>
                  <Badge variant="outline" className="text-[10px] mt-4 text-muted-foreground" data-testid={`guru-pending-${slot}`}>
                    Profile coming soon
                  </Badge>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-12">
        {GURU_IMAGES.map((src, i) => (
          <div key={src} className="rounded-3xl overflow-hidden aspect-[4/3] border border-border">
            <img
              src={src}
              alt="Yoga practice at Immortal Vedas"
              loading="lazy"
              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
              data-testid={`guru-image-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
    </section>
  );
};

export default YogaGurus;
